import * as Sentry from "@sentry/react";

// Injected at build time by vite.config.ts (see `define`). Fall back to
// undefined so dev builds without git context still boot cleanly.
declare const __SENTRY_RELEASE__: string | undefined;
declare const __SENTRY_ENVIRONMENT__: string | undefined;
declare const __SENTRY_GIT_SHA__: string | null | undefined;
declare const __SENTRY_GIT_BRANCH__: string | null | undefined;

const BUILD_RELEASE =
  typeof __SENTRY_RELEASE__ !== "undefined" ? __SENTRY_RELEASE__ : undefined;
const BUILD_ENVIRONMENT =
  typeof __SENTRY_ENVIRONMENT__ !== "undefined" ? __SENTRY_ENVIRONMENT__ : undefined;
const BUILD_GIT_SHA =
  typeof __SENTRY_GIT_SHA__ !== "undefined" ? __SENTRY_GIT_SHA__ : null;
const BUILD_GIT_BRANCH =
  typeof __SENTRY_GIT_BRANCH__ !== "undefined" ? __SENTRY_GIT_BRANCH__ : null;

let initialized = false;

export function initSentry() {
  if (initialized || typeof window === "undefined") return;
  const dsn =
    (import.meta.env.VITE_SENTRY_DSN as string | undefined) ??
    "https://5dc07a51ffb95e9e16bc504516ea32f8@o4510783871254528.ingest.us.sentry.io/4511657002663936";
  if (!dsn) return;
  const isProd = import.meta.env.MODE === "production";
  Sentry.init({
    dsn,
    // Prefer the build-time environment (set by CI/hosting), fall back to Vite mode.
    environment:
      BUILD_ENVIRONMENT ??
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
      import.meta.env.MODE,
    // The release string MUST match what @sentry/vite-plugin used when
    // uploading source maps — otherwise Sentry can't symbolicate stack traces.
    release:
      BUILD_RELEASE ??
      (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ??
      (import.meta.env.VITE_APP_VERSION as string | undefined) ??
      undefined,
    // Performance tracing: sample every transaction in non-prod, 20% in prod.
    tracesSampleRate: isProd ? 0.2 : 1.0,
    // Attach `sentry-trace` + `baggage` headers to outgoing fetch calls hitting
    // our own origin and the marine/weather APIs so distributed traces link up
    // (and CORS-allowed servers can continue the trace).
    tracePropagationTargets: [
      /^\//,
      /^https:\/\/([^/]+\.)?lovable\.(app|dev)/,
      /^https:\/\/api\.open-meteo\.com/,
      /^https:\/\/marine-api\.open-meteo\.com/,
      /^https:\/\/api\.tidesandcurrents\.noaa\.gov/,
      /^https:\/\/overpass-api\.de/,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration({
        // Auto-instruments page loads, navigations, `fetch`, XHR, long tasks,
        // and Web Vitals (LCP/FID/CLS/INP) — no per-call wrapping required.
        enableInp: true,
        enableLongTask: true,
      }),
      Sentry.replayIntegration(),
    ],
  });
  // Tag every event with git provenance so you can filter in Sentry by
  // `git.branch:main` or `git.commit:abc1234`, and pivot Issues → Releases.
  Sentry.setTags({
    "git.commit": BUILD_GIT_SHA ?? "unknown",
    "git.commit_short": BUILD_GIT_SHA ? BUILD_GIT_SHA.slice(0, 7) : "unknown",
    "git.branch": BUILD_GIT_BRANCH ?? "unknown",
  });
  initialized = true;
}

/**
 * Wrap a labeled async operation in a Sentry span so it shows up as a child
 * of the current page-load / navigation transaction. Use this around
 * meaningful client-side work (e.g. building a report, parsing a big
 * payload) that isn't a plain fetch.
 */
export function traceSpan<T>(
  name: string,
  op: string,
  fn: () => Promise<T> | T,
): Promise<T> | T {
  return Sentry.startSpan({ name, op }, fn);
}

export { Sentry };
